/**
 * Admin API client for the Okaiwa identity service.
 *
 * All communication with backend services goes through this client.
 * The client handles authentication, request formatting, and error handling.
 *
 * SECURITY: This client must never request or handle message content.
 * Only metadata and aggregate counts are permitted.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3333'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ContentAction = 'dismiss' | 'warn' | 'remove'
export type ReportAction = 'acknowledge' | 'escalate' | 'resolve'
export type UserStatus = 'active' | 'suspended' | 'pending'
export type AnalyticsTimeRange = '24h' | '7d' | '30d' | '90d'

export interface FlaggedContent {
  id: string
  contentType: 'message_metadata' | 'profile' | 'username' | 'avatar'
  flagReason: string
  reportCount: number
  reporterCount: number
  firstReportedAt: string
  status: 'pending' | 'reviewed'
}

export interface UserAccount {
  id: string
  username: string
  phoneHash: string
  status: UserStatus
  registeredAt: string
  lastActiveAt: string
}

export interface UserListResponse {
  users: UserAccount[]
  total: number
  page: number
  perPage: number
  totalPages: number
}

export interface AbuseReport {
  id: string
  category: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  description: string
  reporterUsername: string
  targetUsername: string
  status: 'open' | 'acknowledged' | 'escalated' | 'resolved'
  createdAt: string
  updatedAt: string
}

export interface WarrantCanaryStatus {
  isActive: boolean
  lastUpdated: string
  nextUpdate: string
  statement: string
}

export interface AnalyticsStats {
  activeUsers: number
  activeUsersChange: number
  messageCount: number
  messageCountChange: number
  walletTransactions: number
  walletTransactionsChange: number
  newRegistrations: number
  newRegistrationsChange: number
}

export interface ChartDataPoint {
  date: string
  count: number
}

export interface WalletChartDataPoint {
  date: string
  sends: number
  receives: number
  swaps: number
}

// ---------------------------------------------------------------------------
// HTTP Client
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

function getAuthToken(): string | null {
  return sessionStorage.getItem('okaiwa_admin_token')
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  }

  const token = getAuthToken()
  if (token) {
    ;(headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(url, {
    ...options,
    headers,
  })

  if (!response.ok) {
    const body = await response.json().catch(() => ({}))
    throw new ApiError(
      (body as Record<string, string>).message ??
        `Request failed with status ${response.status}`,
      response.status,
      (body as Record<string, string>).code,
    )
  }

  return response.json() as Promise<T>
}

// ---------------------------------------------------------------------------
// API Methods
// ---------------------------------------------------------------------------

export const adminApi = {
  auth: {
    login: (email: string, password: string) =>
      request<{ token: string; expiresAt: string }>('/admin/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),

    logout: () => request<void>('/admin/auth/logout', { method: 'POST' }),

    me: () =>
      request<{ id: string; email: string; role: string }>('/admin/auth/me'),
  },

  moderation: {
    getFlaggedContent: (params: { status: string }) =>
      request<FlaggedContent[]>(
        `/admin/moderation/flagged?status=${encodeURIComponent(params.status)}`,
      ),

    takeAction: (contentId: string, action: ContentAction) =>
      request<{ success: boolean }>(`/admin/moderation/${contentId}/action`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      }),
  },

  analytics: {
    getStats: (timeRange: AnalyticsTimeRange) =>
      request<AnalyticsStats>(`/admin/analytics/stats?range=${timeRange}`),

    getActiveUsersChart: (timeRange: AnalyticsTimeRange) =>
      request<ChartDataPoint[]>(
        `/admin/analytics/active-users?range=${timeRange}`,
      ),

    getMessageCountChart: (timeRange: AnalyticsTimeRange) =>
      request<ChartDataPoint[]>(
        `/admin/analytics/message-count?range=${timeRange}`,
      ),

    getWalletActivityChart: (timeRange: AnalyticsTimeRange) =>
      request<WalletChartDataPoint[]>(
        `/admin/analytics/wallet-activity?range=${timeRange}`,
      ),
  },

  users: {
    list: (params: { search: string; page: number; perPage: number }) =>
      request<UserListResponse>(
        `/admin/users?search=${encodeURIComponent(params.search)}&page=${params.page}&perPage=${params.perPage}`,
      ),

    getById: (userId: string) =>
      request<UserAccount>(`/admin/users/${userId}`),

    updateStatus: (userId: string, action: 'suspend' | 'unsuspend') =>
      request<{ success: boolean }>(`/admin/users/${userId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ action }),
      }),
  },

  reports: {
    list: (params: { status: string }) =>
      request<AbuseReport[]>(
        `/admin/reports?status=${encodeURIComponent(params.status)}`,
      ),

    getById: (reportId: string) =>
      request<AbuseReport>(`/admin/reports/${reportId}`),

    takeAction: (reportId: string, action: ReportAction) =>
      request<{ success: boolean }>(`/admin/reports/${reportId}/action`, {
        method: 'POST',
        body: JSON.stringify({ action }),
      }),

    getWarrantCanaryStatus: () =>
      request<WarrantCanaryStatus>('/admin/warrant-canary'),
  },
} as const
